/**
 * Uplifted pieces of `helper/OfflineHelper` (apps/web + apps/mobile) — the COMMON half.
 *
 * This folder MIRRORS the app file it came from and is seeded with only the symbols that have
 * actually been needed so far. The rest of that ~1560-line file (offline login, sale close,
 * table/ticket transfer, the sync sweeps) stays in the apps.
 *
 * The five functions here are byte-identical in both apps modulo quote style and one stray
 * semicolon — diffed function-for-function before collapsing, not assumed. The sixth symbol
 * this batch lifted, `updateSyncedSale`, is NOT here: the two apps clone the sale with
 * different, non-interchangeable functions, so each variant carries its own copy. Each
 * variant's docstring names its OWN clone function; neither names the other platform's,
 * because this file is bundled on both and a comment is indistinguishable from code to a
 * symbol-level bundle grep (ledger, session 19).
 *
 * ⚠️ The startup sweep that drops stale local sales LANDED in session 29, in the desktop variant
 * — it is one-sided, and its two private helpers went with it. It is not here because a shared
 * file cannot hold a body only one platform has.
 *
 * ⚠️ `saveSaleLocally` WAS deliberately not lifted while its only would-be importers were
 * hazard-13 phantoms (web `sale`, web `checkout`, mobile `sale` — each either never calls it or
 * has only commented-out calls). `validateAndSaveSale` below has called it for real since
 * 2026-08-26; it lived PER-VARIANT until D2 (2026-09-16) settled the default `syncStatus` and
 * the `initiatedBy.deviceIdentifier` line on web's side — it is at the bottom of this file now.
 *
 * ── WEB-ADOPTION ASSESSMENT — decisions D2 / D5 (docs/ADOPTION_DECISIONS.md) ─────────────────
 *
 * Examined function-for-function during the web-adoption pass and left split until each
 * difference had a decision, because three of them change persisted data or a safety net.
 * Status on 2026-09-16:
 *
 * 1. ✅ `saveSaleLocally`'s default `syncStatus`: **D2 taken — web's SYNCED on both**, with the
 *    three mobile transfer paths (the only mobile writers that relied on the old default)
 *    stamping their sales explicitly. Lives here now.
 * 2. ✅ `isEnableOfflineModeFeature`: **D5a taken — web's flag on both**, and mobile writes the
 *    flag at boot so it stays on. Lives here now.
 * 3. ✅ `voidSaleOffline` `statusInfo`: **D5b taken — web's `statusInfo = reason`**. `reason`
 *    arrives as the `{ reason, remarks }` object from either platform's `ChooseReason`, so the
 *    stored shape is now the same flat object on both; mobile used to nest it once more. Still
 *    per-variant for one line: mobile toasts on a missing sale.
 * 4. ✅ The clone: **D5c taken — `structuredClone` on both** (mobile shims it at boot with lodash
 *    `cloneDeep`, which keeps `Date`; its SQLite store stringifies on write, so nothing on disk
 *    changes). `updateSyncedSale`, `saveSaleOffline` and `saleCloseOffline` differed by the clone
 *    alone and now live HERE, once.
 *
 * Genuinely blocked, not permanent — revisit when the named blocker clears:
 *   · `getOptionsetOffline` — one line apart; web dispatches `optionSetsDataCallComplete`,
 *     which mobile's `catalogueSlice` does not define. Collapses the day that action exists.
 *   · `logoutOffline` — one line apart; web calls `logout()` at the end and mobile does not.
 *     Whether mobile SHOULD is a product question about offline logout, not an uplift.
 *
 * ⚠️ Flagged, not fixed: `voidSaleOffline` on web writes the magic string `"tobesynced"` where
 * mobile uses `SALE_SYNC_STATUSES.TO_BE_SYNCED`. Same value (checked), so it is cosmetic — but
 * it is the only reason that one line reads as divergent.
 */
import { isEmpty } from "lodash";
import { get, replace, put, del } from "../../utils/OfflineUtil";
import { backendErrorMap } from "../../utils/backendErrors";
import { DEVICEIDENTIFIER, SALE_PROCESS_OPERATION_TYPES, SALE_SYNC_STATUSES, TICKET_PRINT_STATUS_TYPES } from "../../utils/constants/shared";
// a split folder (each platform has its own key table) — the sanctioned exception, resolved per platform
import { LOCALSTORAGE_KEYS } from "../../utils/constants";
import { notifyError } from "../../effects-bridge";
import store from "../../redux-store";
import { customerRecordNotFound, setExternalLoyaltyData } from "../../redux-store/slices/CustomerSlice";
import { checkCreateTicket, resetSaleData, updateSaleMetaInfo } from "../saleHelpers";
import { CREATE_TICKET_ON_SAVE } from "../../utils/constants";
import { REST_SALE_BACK_ITEMS_NOT_PROCESSED } from "../../data/constants/shared";
// `saveSaleLocally` is NOT imported here: it is defined in this file since D2 (2026-09-16), which
// release_4.0.0 has not got — its `updateAllTicketsPrintStatus` below calls this file's copy.
import { fetchOfflineSale } from "../OfflineHelper";
import moment from "moment-timezone";
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";
import { getLocalStorage } from "../../utils/localStorage";

/**
 * Turn a backend error code into a human message and toast it.
 *
 * ⚠️ **`backendErrorMap` is a DIVERGENT symbol and this is a `shared.ts`, so the import above
 * names the FOLDER, not a variant** (ledger hazard 16): five error codes carry different
 * wording per platform, and pinning a variant here would show the other platform's strings.
 * The runtime probe asserts WHICH map the message came from, not just that a toast fired.
 *
 * ⚠️ The toast itself is the injected `notifyError` — core cannot import UI. Both apps wire it
 * to their own `ToastNotifyError` in `bootstrapCoreBridges.ts`, so this is a symmetric mapping,
 * not a behaviour change. The bare `catch` swallowing a toast failure is the apps' own and is
 * preserved verbatim.
 */
export function handleOfflineError(msg: any) {
  try {
    const msgStr = msg && (backendErrorMap as any)[msg] ? (backendErrorMap as any)[msg] : msg;
    notifyError(msgStr);
  } catch (error) {
    console.log("handleOfflineError");
  }
}

/**
 * Write a sale back to the local SALE store, repairing its `syncStatus` first.
 *
 * The two repairs are the apps' own, with their comment preserved: a sale whose `operation` is
 * `SAVE_SALE` but which claims to be SYNCED is downgraded to TO_BE_SYNCED, and a sale with no
 * `syncStatus` at all is marked SYNCED.
 *
 * ⚠️ Mutates `params.sale` in place before writing — both apps do, and `updateSyncedSale`
 * relies on having cloned first. Preserved.
 */
export function replaceLocalSale(params: any) {
  const { sale, callback, source } = params;
  // Observed some instances where operation is set to SAVE_SALE but syncStatus is not tobesynced.
  if (
    sale.operation === SALE_PROCESS_OPERATION_TYPES.SAVE_SALE &&
    (!sale.syncStatus || sale.syncStatus === SALE_SYNC_STATUSES.SYNCED)
  ) {
    sale.syncStatus = SALE_SYNC_STATUSES.TO_BE_SYNCED;
  }
  if (!sale.syncStatus) {
    sale.syncStatus = SALE_SYNC_STATUSES.SYNCED;
  }
  replace({
    domain: "SALE",
    values: {
      key: sale.mid,
      value: sale,
    },
    callbackFn: (returnedSale: any, error: any) => {
      console.log("replaceLocalSale==>", returnedSale);
      if (error) {
        handleOfflineError(error);
        return;
      }
      if (callback) {
        callback(returnedSale);
      }
      // sale summary
      // updateOfflineSaleStore(returnedSale);
    },
  });
}

/**
 * Bulk-write option sets to the local OPTIONSET store, keyed by `mid`.
 *
 * ⚠️ Note the callback signature is `(status, error)` here and `(error, status)` in
 * `putItemsOffline` below — the two apps disagree with each other about which order
 * `OfflineUtil.put` uses, identically on both platforms. Preserved verbatim on both sides
 * rather than "corrected"; the practical effect is only which value the
 * `!== "OFFLINE_DB_NOT_AVAILABLE"` guard inspects, and both branches are no-ops today.
 */
export function saveOptionSetsOffline(items: any) {
  const keyValues: any[] = [];
  items = items || [];
  items.forEach(function (item: any) {
    keyValues.push({
      key: item.mid,
      value: item,
    });
  });
  put({
    domain: "OPTIONSET",
    values: keyValues,
    callbackFn: (status: any, error: any) => {
      if (error) {
        if (error !== "OFFLINE_DB_NOT_AVAILABLE") {
          // Ext.Msg.alert('', error);
        }
        return;
      }
      // if (Rista.AppConstants.getDeviceReady()) {
      //     if (!Ext.isEmpty(modificationStatus.lastModified)) {
      //         ristaLocalStorage.set('optionSetLastModified', modificationStatus.lastModified);
      //     }
      // }
      // if (cb) {
      //     cb.apply(scope);
      // }
    },
    // if (Rista.AppConstants.getDeviceReady()) {
    //     if (!Ext.isEmpty(modificationStatus.lastModified)) {
    //         ristaLocalStorage.set('optionSetLastModified', modificationStatus.lastModified);
    //     }
    // }
    // if (cb) {
    //     cb.apply(scope);
    // }
  });
}

/** Bulk-write catalog items to the local ITEM store, keyed by `mid`. */
export function putItemsOffline(items: any) {
  const keyValues: any[] = [];
  items = items || [];
  if (items?.length) {
    items.forEach(function (item: any) {
      keyValues.push({
        key: item.mid,
        value: item,
      });
    });
  }
  put({
    domain: "ITEM",
    values: keyValues,
    callbackFn: (error: any, status: any) => {
      if (error) {
        if (error !== "OFFLINE_DB_NOT_AVAILABLE") {
          // Ext.Msg.alert('', error);
        }
        return;
      }
      // if (Rista.AppConstants.getDeviceReady()) {
      //     if (!Ext.isEmpty(modificationStatus.lastModified)) {
      //         ristaLocalStorage.set('optionSetLastModified', modificationStatus.lastModified);
      //     }
      // }
      // if (cb) {
      //     cb.apply(scope);
      // }
    },
  });
  // ristaLocalStorage.remove('itemLastModified');
  // del({
  //     domain: 'ITEM',
  //     callbackFn: (error) => {
  //     if (error) {
  //         if (error !== 'OFFLINE_DB_NOT_AVAILABLE') {
  //             //Ext.Msg.alert('', error);
  //         }
  //         return;
  //     }
  //     let keyValues = [];
  //     items = items || [];
  //     items.forEach(function (item) {
  //         keyValues.push({
  //             key: item.mid,
  //             value: item
  //         });
  //     });
  //     put({
  //         domain: 'ITEM',
  //         values: keyValues,
  //         callbackFn: (error, status) => {
  //         if (error) {
  //             if (error !== 'OFFLINE_DB_NOT_AVAILABLE') {
  //                 //Ext.Msg.alert('', error);
  //             }
  //             return;
  //         }
  //         // if (Rista.AppConstants.getDeviceReady()) {
  //         //     if (!Ext.isEmpty(modificationStatus.lastModified)) {
  //         //         ristaLocalStorage.set('optionSetLastModified', modificationStatus.lastModified);
  //         //     }
  //         // }
  //         // if (cb) {
  //         //     cb.apply(scope);
  //         // }
  //     }});
  // }});
}

/**
 * Write one keyed blob to the local OTHERS store — the catch-all domain the catalog sagas use
 * for channels, charges, taxes and the like. `ip.type` is the key.
 *
 * ⚠️ `ip.callback` is destructured and never called, in BOTH apps. Left as-is: it is not the
 * only thing separating the platforms, so standing rule 2 says flag rather than remove.
 */
export function saveOthersOffline(ip: any) {
  const { type, items, callback } = ip;
  try {
    put({
      domain: "OTHERS",
      values: {
        key: type,
        value: items,
      },
      callbackFn: function (status: any, error: any) {
        if (error) {
          handleOfflineError(error);
          return;
        }
      },
    });
  } catch (error) {
    console.log("saveOthersOffline -->", type, error);
  }
}

/**
 * Read one "others" catalogue domain out of the local store and hand it to `callback`.
 *
 * ⚠️ **Byte-identical in both apps** apart from how each spells lodash (`isEmpty` vs
 * `_.isEmpty`) — an import style, not a behaviour — so this body is shared. Diffed
 * line-for-line before collapsing, not assumed.
 *
 * ⚠️ Three swallow-and-continue behaviours are the apps' own and are preserved verbatim:
 * an `error` toasts through `handleOfflineError` and returns WITHOUT calling back; an empty
 * result returns without calling back either (so the caller's callback simply never fires,
 * rather than receiving `[]`); and the outer `try/catch` logs to `console.log` and drops
 * everything else. A caller that relies on always being called back is already broken today.
 *
 * ⚠️ The commented-out `forEach`/`Ext.isEmpty` blocks in both apps are NOT carried over — they
 * are pre-monorepo residue, and a comment cannot change behaviour.
 */
export function fetchOthersOffline(ip: any) {
  const { type, callback } = ip;

  try {
    get({
      domain: "OTHERS",
      keys: type,
      callbackFn: function (items: any, error: any) {
        let fetchedItems = [];
        if (error) {
          handleOfflineError(error);
          return;
        }
        if (isEmpty(items)) {
          return;
        }
        fetchedItems = JSON.parse(items);
        if (callback) {
          callback(fetchedItems);
        }
      },
    });
  } catch (error) {
    console.log("fetchOthersOffline -->", type, error);
  }
}

/**
 * Run a named filter over the local SALE store and hand the parsed sale VOs to `params.cb`.
 *
 * ⚠️ **The two apps' bodies are identical apart from lodash spelling and ONE stray
 * `console.log("filterRemoteStore===getOpenOrderFn==")` on mobile.** That log is the only thing
 * that made the two platforms differ, so it is removed under ledger rule 2(a) and the body is
 * shared — the same call the `isCustomerDataFetching` precedent made. It is a debug breadcrumb
 * with no effect on state; **if you want it back, it belongs in `index.native.ts` with its own
 * copy of the body, and this note is the record that it was dropped deliberately.**
 *
 * ⚠️ Note `params.filter` is spread AFTER `name`/`transform`, so a caller CAN override either;
 * `params.filter.data` is spread into `params` separately. Preserved exactly — the key order is
 * load-bearing.
 *
 * ⚠️ Both the empty and non-empty paths call back, but a hard `error` returns silently (the
 * apps' `Ext.Msg.alert` line is commented out upstream). Preserved.
 */
export function filterRemoteStore(params: any) {
  let filteredSaleVos: any[] = [];

  get({
    domain: "SALE",
    filter: {
      params: { ...params.filter.data },
      name: "SALE",
      transform: "SALE_VO",
      ...params.filter,
    },
    callbackFn: (saleVos: any, error: any) => {
      if (error) {
        return;
      }
      if (!isEmpty(saleVos)) {
        saleVos.forEach((item: any) => {
          filteredSaleVos.push(JSON.parse(item));
        });
        if (params.cb) {
          params.cb({ results: filteredSaleVos });
        }
      } else {
        if (params.cb) {
          params.cb({ results: [] });
        }
      }
    },
  });
}

/**
 * Build the table-view occupancy list offline: every resource is reported Free or Occupied with
 * the sale sitting on it.
 *
 * ⚠️ **Byte-identical in both apps** apart from lodash spelling. Reads `tableViewStore.resources`
 * through the STORE BRIDGE, as the apps read their own `store` singleton.
 *
 * ⚠️ Three quirks preserved verbatim:
 * - a non-array `resources` returns EARLY and never calls back (the apps' comment says "Throw
 *   error resources are not loaded"; nothing is actually thrown);
 * - `resourceIds` is computed TWICE, once inside the `Array.isArray` guard and again after it —
 *   redundant, but harmless and the apps' own;
 * - the Occupied branch indexes `results[resourceIndex]` with an index obtained from
 *   `occupiedResourceIds`. That looks like a positional-alignment bug and is NOT one:
 *   `occupiedResourceIds` is a 1:1 `results.map(...)`, so the two arrays are aligned by
 *   construction and `results[resourceIndex]` is always the matching sale. Checked against a
 *   probe case where the occupied resource is NOT at index 0. Noted here because it reads like
 *   a defect and will otherwise be "fixed" by someone.
 */
export function fetchTableOffline(params: any) {
  var resources = store.getState().tableViewStore?.resources,
    resourceIds: any[] = [],
    resourceStatusArr: any[] = [],
    currentStatus,
    occupiedResourceIds: any[] = [],
    saleVo: any,
    resourceIndex;

  if (Array.isArray(resources)) {
    resourceIds = resources?.map((resource: any) => {
      return resource.mid;
    });
  } else {
    // Throw error resources are not loaded
    return;
  }

  resourceIds = resources.map((resource: any) => {
    return resource.mid;
  });

  filterRemoteStore({
    filter: {
      name: "SALES_WITH_RESOURCES",
      transform: "SALE_RESOURCEID",
    },
    cb: ({ results }: any) => {
      if (isEmpty(results)) {
        resourceStatusArr = resourceIds.map((resourceId: any) => {
          return { resourceId: resourceId, currentStatus: "Free", orders: [] };
        });
      } else {
        occupiedResourceIds = results.map(
          (res: any) => res.resourceId || res.serviceLabel?.resourceId
        );
        resourceIds.forEach((rid: any) => {
          saleVo = null;
          resourceIndex = occupiedResourceIds.indexOf(rid);
          if (resourceIndex > -1) {
            currentStatus = "Occupied";
            saleVo = results[resourceIndex]?.sale;
            if (saleVo?.printLog?.length && !(saleVo?.printed === false || saleVo?.printed === true)) {
              saleVo.printed = true;
            }
          } else {
            currentStatus = "Free";
          }
          resourceStatusArr.push({
            resourceId: rid,
            currentStatus,
            orders: saleVo ? [saleVo] : [],
          });
        });
      }
      if (params && params.cb) {
        params.cb(resourceStatusArr);
      }
    },
  });
}

/**
 * Read EVERY sale out of the local store and hand them to `params.cb`.
 *
 * ⚠️ **Byte-identical in both apps** (normalised diff, not assumed). Note it calls back with `[]`
 * on an empty store rather than returning silently — unlike `fetchOthersOffline` above, which
 * does return silently. The asymmetry is the apps' own; the startup sweep relies on always being
 * called.
 */
export function fetchAllOfflineSales(params: any) {
  const { cb } = params;
  get({
    domain: 'SALE',

    callbackFn: (sales: any, error: any) => {
      if (error) {
        handleOfflineError(error);
        return;
      }
      if (!sales?.length) {
        cb([]);
      } else
        cb(sales);
    }
  })
}

/**
 * Delete one or more sales from the local store by id.
 *
 * ⚠️ **Byte-identical in both apps.** `saleId` may be a single id or an ARRAY — the startup sweep
 * passes the whole removable list at once. The comment "remove sale from reducer" is the apps'
 * own and describes what the optional callback is for; this function does not touch state.
 */
export function removeOfflineSale(params: any) {
  const { saleId, cb } = params;

  del({
    domain: 'SALE',
    keys: saleId,
    callbackFn: (removedSale: any, error: any) => {
      if (error) {
        handleOfflineError(error);
        return;
      }
      //remove sale from reducer
      if (cb) {
        cb(saleId);
      }
    }
  });
}

/**
 * Validate a sale for OFFLINE saving, then persist it locally.
 *
 * Three steps, in order: refuse the save if any bill discount carries an unexercised coupon;
 * stamp the sale's offline metadata (unless it is a no-charge transaction); and — when the sale
 * is attached to a resource (a table) and is not a sub-sale — check the local SALE store for a
 * DIFFERENT open sale on the same resource before writing, so two bills cannot claim one table.
 *
 * SHARED: the two app bodies are identical modulo `_.isEmpty` vs `isEmpty` — the same lodash
 * function, imported differently. Diffed token-by-token after whitespace normalisation; nothing
 * else differs, including the commented-out blocks, which are preserved.
 *
 * ⚠️ **The `UNEXERCISED_COUPON_EXISTS` guard does not actually stop the save.** Its `return` exits
 * the `forEach` CALLBACK, not `validateAndSaveSale`, so the sale is toasted and then written
 * anyway. That is the apps' behaviour on both platforms and is transcribed as-is — fixing it
 * would block saves that succeed today.
 *
 * `params.onOfflineFailure` (decision 95, optional, passed only by v2's discount sheet) is called once on each
 * failure that never reaches `callback`: the local read failing, a different open sale holding the table, and
 * the local write failing (`saveSaleLocally`). It is a NEW name, not `handleError`, because two mobile v1
 * screens already pass `handleError` to the save and must behave exactly as before. The toast stays as it is.
 *
 * 🔴 **`saveSaleLocally` is imported from the FOLDER, not a variant** — it is per-variant, and a
 * `shared.ts` must let the bundler pick. Same sanctioned exception as `updateSaleMetaInfo` above;
 * the resulting `shared -> index.<platform> -> shared` cycle is safe only because both calls
 * happen at CALL time. Never move either to module scope.
 */
export function validateAndSaveSale(params: any, isComingForSaleClose = false) {
  let { sale, callback, onOfflineFailure } = params;
  let resourceDuplicateSaleFound = false,
    saleResourceId;

  if (!isEmpty(sale.billDiscountList)) {
    sale.billDiscountList.forEach((billDisc: any) => {
      if (!isEmpty(billDisc.couponId) && !billDisc.appliedBy) {
        handleOfflineError("UNEXERCISED_COUPON_EXISTS");
        return;
      }
      // if (isEmpty(disc?.couponId) && disc?.appliedBy) {
      //   handleOfflineError("UNREDEEMED_LOYALTY_EXISTS");
      //   return;
      // }
    });
  }

  if (sale.transactionType !== "NC") {
    //OFFLINE TODO:
    // result = Pos.SaleHelper.calculateAndValidateSale(sale, true);
    // if (!Ext.isEmpty(result.code)) {
    //     Rista.CommonHelper.handleOfflineError(result.code, cb, scope);
    //     return;
    // }
    // sale = result.message;
    sale = updateSaleMetaInfo(sale);
  } else {
    //Pos.SaleHelper.calculateAndUpdateSale(sale);
  }

  if (sale.serviceLabel && !isEmpty(sale.serviceLabel.resourceId) && !sale.originalSaleInfo) {
    saleResourceId = sale.serviceLabel.resourceId;
    get({
      domain: "SALE",
      filter: {
        name: "SALE",
        params: {
          status: "Open",
          resourceId: saleResourceId,
          restrictUserOthersOrders: false,
        },
      },
      callbackFn: (fetchedSales: any, error: any) => {
        if (error) {
          //Rista.CommonHelper.handleOfflineError(error, cb, scope);
          onOfflineFailure?.();
          return;
        }
        fetchedSales.forEach(function (fetchedSale: any) {
          fetchedSale = JSON.parse(fetchedSale);
          if (sale.mid !== fetchedSale.mid && !isComingForSaleClose) {
            resourceDuplicateSaleFound = true;
          }
        });
        if (resourceDuplicateSaleFound) {
          handleOfflineError("LABEL_ALREADY_ATTACHED_OFFLINE");
          onOfflineFailure?.();
          return;
        }
        processSalePreorders(sale);
        saveSaleLocally({ sale, callback, onOfflineFailure });
      },
    });
    return;
  }
  processSalePreorders(sale);
  saveSaleLocally({
    sale,
    callback,
    onOfflineFailure,
  });
}

/**
 * Mark every preorder attached to this sale as `Accepted` and bind it to the sale, then strip
 * `preorderIds` off the sale.
 *
 * SHARED: identical in both apps modulo the lodash import style.
 *
 * MODULE-PRIVATE, exactly as in both apps: neither exports it, and `validateAndSaveSale` above is
 * its only caller. Not exported here either, so core's public surface gains nothing.
 *
 * ⚠️ **The accepted preorder is never written back.** The line that would persist it
 * (`updatePreorderOffline`) is commented out in BOTH apps, so the loop mutates a local object and
 * discards it; only the `delete sale.preorderIds` at the end has an effect. Transcribed as-is —
 * this is the offline half of a kiosk feature that was never finished.
 */
function processSalePreorders(sale: any) {
  const preorderIds = sale.preorderIds;
  if (isEmpty(preorderIds)) {
    return;
  }
  preorderIds.forEach((preOrderId: any) => {
    get({
      domain: "PREORDER",
      keys: preOrderId,
      callbackFn: (preorder: any, error: any) => {
        if (error) {
          handleOfflineError(error);
          return;
        }
        preorder = JSON.parse(preorder);
        preorder.status = "Accepted";
        preorder.saleClientId = sale.mid;
        //Pos.service.KioskService.updatePreorderOffline(preorder);
      },
    });
  });
  delete sale.preorderIds;
}


/**
 * Stamp an offline sale's event log with a new fulfillment status and queue it for sync.
 *
 * ⚠️ CONVERGED onto WEB's body (2026-09-08). The two apps' copies were the same function with
 * three differences, every one of them a defect or a house-rule violation on the mobile side
 * rather than a platform need:
 *
 *  1. `window.RL.saleutil` (web) / `globalThis.saleutil` (mobile) — the SAME module reached by
 *     two different globals. Both are gone: the module is IMPORTED, per CLAUDE.md's rista-library
 *     rule and `pnpm audit:uplift` check K. This also drops core's dependency on an app bootstrap
 *     having run first.
 *  2. Mobile had `auditInfo.deviceIdentifier` COMMENTED OUT, so offline status updates from that
 *     platform carried no device id into the audit trail. Web's line is restored for both — this
 *     is the adoption, and it changes what mobile writes to the audit log.
 *  3. Mobile read `businessData.deviceInfo.label` unguarded where web optional-chains it; web's
 *     safer form wins, since `deviceInfo` is genuinely absent on a freshly provisioned device.
 *
 * `sale.syncStatus` uses the `SALE_SYNC_STATUSES.TO_BE_SYNCED` constant (mobile's form) rather
 * than web's `'tobesynced'` literal — same value, and the constant is already core's.
 */
export function updateFulfillmentStatusOffline({ saleId, payloadToSend, callback }: any) {
  let auditInfo: any = {},
    fulfillmentStatus = payloadToSend.fulfillmentStatus,
    businessStore = store.getState().businessStore;
  const businessData: any = businessStore?.businessData || {},
    userData: any = businessStore?.userData || {};

  fetchOfflineSale({
    saleId,
    callback: (sale: any) => {
      if (!sale) {
        notifyError("Sale not found!");
        return;
      }
      auditInfo.date = businessData?.timeZone?.zoneId
        ? moment().tz(businessData.timeZone.zoneId).format()
        : "";
      auditInfo.buid = userData.businessUserId;
      auditInfo.userName = userData.name;
      auditInfo.userId = userData.userId;
      auditInfo.transactionEnd = businessData.transactionEnd;
      auditInfo.deviceIdentifier = getLocalStorage("deviceIdentifier");
      auditInfo.deviceLabel = businessData?.deviceInfo?.label;
      saleutil.updateEventLog(sale, { audit: auditInfo, fulfillmentStatus: fulfillmentStatus });
      sale.syncStatus = SALE_SYNC_STATUSES.TO_BE_SYNCED;
      sale.operation = "SAVE_SALE";
      saveSaleLocally({ sale, callback });
    },
  });
}

// ── Converged 2026-09-16 (D5c, docs/ADOPTION_DECISIONS.md) ────────────────────────────────
// These three differed between the variants by the clone alone: web `structuredClone`, mobile a
// JSON round-trip. D5c took web's — mobile installs a `structuredClone` shim at boot
// (`apps/mobile/src/utils/hermesPolyfill.js`, lodash `cloneDeep`, keeps `Date`), and its SQLite
// store stringifies on write anyway, so nothing on disk changes there. One body each, here.

/**
 * Mark a sale SYNCED and write the copy back to the local store. The `channel === "Online"`
 * early return is the apps' own: aggregator orders are never written to the local store.
 */
export function updateSyncedSale(sale: any) {
  if (sale.channel === "Online") {
    return;
  }
  const saleCopy = structuredClone(sale);
  saleCopy.syncStatus = SALE_SYNC_STATUSES.SYNCED;
  replaceLocalSale({ sale: saleCopy });
}

/**
 * Deep-clone the sale, decide whether it should create kitchen tickets, default its status to
 * `Open`, and hand it to `validateAndSaveSale`.
 */
export function saveSaleOffline(params: any) {
  let saleData = structuredClone(params.sale);
  if (saleData.saleStatus === "Open") {
    saleData["createTickets"] = checkCreateTicket(CREATE_TICKET_ON_SAVE);
    // saleData.saleStatus = 'Open';
  }
  if (!saleData.saleStatus) {
    saleData.saleStatus = "Open";
  }
  validateAndSaveSale({
    sale: saleData,
    callback: params.callback,
    onOfflineFailure: params.onOfflineFailure,
  });
}

/**
 * Close a sale offline: refuse if back-ordered items are unprocessed (when inventory is
 * checked) or items are on hold, else mark it Closed with close-time tickets and save it.
 * Uplifted 2026-09-15 for `myOrders/useOrderDetail.close`.
 */
export function saleCloseOffline(params: any) {
  let saleData = structuredClone(params.sale);
  const checkInventoryOnSale = store.getState().businessStore?.businessData?.checkInventoryOnSale,
    fulfillmentStatusArr = ["Processed", "Dispatched", "Delivered"],
    backOrderedItems: any[] = [],
    onHoldItems: any[] = [];

  if (checkInventoryOnSale) {
    saleData.saleItems.forEach(function (saleItem: any) {
      if (saleItem.quantity && saleItem.isBackOrder) {
        backOrderedItems.push(saleItem);
      }
    });
    if (!isEmpty(backOrderedItems) && fulfillmentStatusArr.indexOf(saleData.fulfillmentStatus) === -1) {
      handleOfflineError(REST_SALE_BACK_ITEMS_NOT_PROCESSED);
      return;
    }
  }
  saleData.saleItems.forEach(function (saleItem: any) {
    if (saleItem.quantity && saleItem.onHold) {
      onHoldItems.push(saleItem);
    }
  });
  if (!isEmpty(onHoldItems)) {
    handleOfflineError("REST_SALE_ITEMS_ON_HOLD");
    return;
  }

  //Calculate and update will populate all the fields including payments, but the status needs to be set to Closed.
  saleData.saleStatus = "Closed";
  saleData["createTickets"] = checkCreateTicket("Close");
  validateAndSaveSale(
    {
      sale: saleData,
      callback: params.callback,
    },
    true
  );
}

// ── Converged 2026-09-16 (D2, D5a — docs/ADOPTION_DECISIONS.md) ───────────────────────────
// Web's bodies. What changed for mobile, and why it is safe:
//   · `saveSaleLocally`: a sale arriving with NO `syncStatus` is now stamped SYNCED (was
//     TO_BE_SYNCED). Everything mobile must push already stamps itself (`operation = "SAVE_SALE"`
//     forces TO_BE_SYNCED two lines below); the three ticket/item/table transfer paths in
//     `apps/mobile/src/helper/OfflineHelper.js`, which used to rely on the default, stamp their
//     sales explicitly since the same day. Mobile's offline sales also gain
//     `initiatedBy.deviceIdentifier` (its register saga stores the key; the app had the line
//     commented out).
//   · `isEnableOfflineModeFeature`: reads the `isShowOfflineModeToggle` flag on both. Mobile
//     writes it at boot (`apps/mobile/src/bootstrapCoreBridges.ts`) the way the desktop shell
//     does on `app.version`, so it stays "always true" there in effect.

/**
 * Write a sale to the local store: stamp its sync status, offline number, ticket flags and
 * `initiatedBy`, then `put` it and run the callbacks.
 *
 * ⚠️ `checkCreateTicket` is called with the bare literals `"Save"` / `"Close"` rather than the
 * `CREATE_TICKET_ON_SAVE` constant used elsewhere. Both apps did exactly that; transcribed.
 *
 * ⚠️ The `source` field is destructured and never used — the apps' own dead binding, kept so the
 * shape of `params` stays documented.
 */
export function saveSaleLocally(params: any) {
  let { sale, callback, sagaCallback, source, onOfflineFailure } = params;
  const now = new Date().getTime().toString();
  //Observed some instances where operation is set to SAVE_SALE but syncStatus is not tobesynced.
  if (sale.operation === "SAVE_SALE" && (!sale.syncStatus || sale.syncStatus === SALE_SYNC_STATUSES.SYNCED)) {
    sale.syncStatus = SALE_SYNC_STATUSES.TO_BE_SYNCED;
  }
  if (!sale.syncStatus) {
    sale.syncStatus = SALE_SYNC_STATUSES.SYNCED;
  }
  if (!sale.billNumber && !sale.offlineNumber) {
    sale.offlineNumber = now.substr(2, 8);
  }
  if (sale.saleStatus === "Open") {
    sale["createTickets"] = checkCreateTicket("Save");
  } else if (sale.saleStatus === "Closed") {
    sale["createTickets"] = checkCreateTicket("Close");
  }
  if (!sale.initiatedBy) {
    let businessStore = store.getState().businessStore;
    sale["initiatedBy"] = {
      "buid": !!businessStore?.userData?.businessUserId ? businessStore.userData.businessUserId : "",
      "businessDay": moment().format("YYYY-MM-DD"),
      "date": moment.utc().format(),
      "deviceIdentifier": getLocalStorage(DEVICEIDENTIFIER),
      "deviceLabel": !!businessStore?.businessData?.deviceInfo ? businessStore?.businessData?.deviceInfo.label : "",
      "userId": !!businessStore?.userData?.userId ? businessStore.userData.userId : "",
      "userName": !!businessStore?.userData?.name ? businessStore.userData.name : {},
    };
  }
  sale["transactionType"] = "Sale";
  console.log("===============ticketGroup  SAVING SALE LOCALLY", sale);
  put({
    domain: "SALE",
    values: {
      key: sale.mid,
      value: sale,
    },
    callbackFn: function (returnedSale: any, error: any) {
      if (error) {
        handleOfflineError(error);
        onOfflineFailure?.();
        return;
      }
      //Rista.CommonHelper.updateOfflineSaleStore(returnedSale);
      //TODO common sale success handles
      resetSaleData();
      if (sagaCallback) {
        sagaCallback(returnedSale);
      }
      if (callback) {
        callback(returnedSale);
      }
    },
  });
}

/** Whether the offline-mode feature is switched on for this device. */
export function isEnableOfflineModeFeature() {
  return getLocalStorage(LOCALSTORAGE_KEYS.isShowOfflineModeToggle) ? true : false;
}
/**
 * Write kitchen-ticket print statuses onto a LOCALLY stored sale, and queue the sale for sync.
 *
 * Byte-identical in both apps' `helper/OfflineHelper` (one guard is written in the other order on
 * mobile — `Array.isArray(ticketInfos) && ticketInfos.length` — equivalent for every caller, all of
 * which pass an array; web's order is kept). Uplifted 2026-09-15 for `@rista/core/printing`, whose
 * status sink makes v1's offline KOT claim through it. Both apps re-export this definition.
 *
 * `cb(returnedSale)` on success, `cb(null, error)` when the sale cannot be read or does not exist,
 * and `cb(returnedSale)` carrying a `code` when saving fails — v1's printKOT prints only when
 * neither `res.code` nor `err` is set.
 *
 * 🔴 `fetchOfflineSale` / `saveSaleLocally` are imported from the FOLDER, not a variant — they are
 * per-variant (same rule as `validateAndSaveSale` above). The toast is the injected `notifyError`.
 */
export function updateAllTicketsPrintStatus(params: any) {
  const { id, ticketInfos, sync = true, cb, printSource } = params;

  fetchOfflineSale({
    saleId: id,
    callback: (sale: any, error: any) => {
      console.log("===============ticketGroup updateTicketPrintStatus sale---------", sale, params);
      if (error) {
        handleOfflineError(error);
        cb?.(null, error);
        return;
      }
      if (!sale) {
        notifyError("Sale not found!");
        cb?.(null, true);
        return;
      }
      if (ticketInfos.length && Array.isArray(ticketInfos)) {
        ticketInfos.forEach((ticketData: any) => {
          sale.tickets.forEach(function (saleTicket: any) {
            if (saleTicket.ticketNumber === ticketData?.ticketNumber) {
              saleTicket.printStatus = ticketData?.printStatus;
              if (saleTicket.printStatus === TICKET_PRINT_STATUS_TYPES.COMPLETED) {
                saleTicket.printed = true;
              }
            }
          });
        });
      }
      if (sync) {
        sale.syncStatus = SALE_SYNC_STATUSES.TO_BE_SYNCED;
        sale.operation = SALE_PROCESS_OPERATION_TYPES.SAVE_SALE;
      }
      saveSaleLocally({
        sale,
        callback: (returnedSale: any) => {
          console.log("===============ticketGroup updateTicketPrintStatus returnedSale---------", returnedSale);
          if (returnedSale.code) {
            handleOfflineError(returnedSale.code);
            cb?.(returnedSale);
            return;
          }
          if (cb) {
            cb(returnedSale);
          }
        },
        source: printSource,
      });
    },
  });
}

/**
 * The customer search offline: no lookup, so "not found", and no provider view either. Uplifted
 * 2026-09-29 with the facade's `customerSearch` (My Orders v2's shared customer lookup). WEB's
 * body on both, as web supersedes mobile: mobile's did not clear the loyalty data, which left the
 * previous customer's provider view in place after an offline search.
 */
export function customerSearchOffline(): void {
  store.dispatch(customerRecordNotFound());
  store.dispatch(setExternalLoyaltyData(null));
}
